/**
 * Live signals from the database (migration 203; founder, 2026-10-29: "make
 * Inquiries and Chat real time"). When a chat message is saved or a request
 * changes, the database sends a signal on a private Supabase Realtime
 * channel, and a page listening re-reads what's new right away.
 *
 *   'desk'               the Inquiries Desk (admins and the concierge role)
 *   'thread:<thread id>' one customer's own conversation
 *
 * A signal carries ids only, never a message: the page still fetches from
 * the server. Its timed re-reads stay as the fallback, so a channel that
 * can't connect costs nothing but speed.
 */
import { supabase } from '@/lib/supabase';

/**
 * Listen on `topic`. `onSignal(payload, event)` runs for each signal.
 * Returns a function that stops listening.
 */
export function listenForSignals(topic, onSignal) {
  let channel = null;
  let stopped = false;
  (async () => {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token || stopped) return;
      // Private channels check the signed-in user against the policies.
      await supabase.realtime.setAuth(token);
      if (stopped) return;
      channel = supabase
        .channel(topic, { config: { private: true } })
        .on('broadcast', { event: '*' }, (message) => {
          try {
            onSignal(message?.payload || {}, message?.event || null);
          } catch {
            /* a page's handler never breaks the channel */
          }
        })
        .subscribe();
    } catch {
      /* no live channel: the timed re-reads still cover it */
    }
  })();
  return () => {
    stopped = true;
    if (channel) {
      try {
        supabase.removeChannel(channel);
      } catch {
        /* already gone */
      }
    }
  };
}
